import { defineStore } from 'pinia'
import { recordSaveCoordinatorEvent, recordSaveRetryInvoked } from '@/util/canvas-save-telemetry'
import ApiClient from '@/util/ApiClient'
import {
  claimNetworkError,
  dismissUnsavedChangeRetrySnackbar,
  enqueueUnsavedChangeRetrySnackbar,
  enqueueUnsavedChangeSnackbar
} from '@/util/api-error-snackbar'
import { getApiErrorStatus, isNetworkError } from '@/util/api-error'
import { onClearAllStores, useAuthStore } from '@/modules/auth/stores/AuthStore'
import Element from '../models/Element'
import { noteCreateParked, ParentCreateParked, useElementStore } from './ElementStore'

/**
 * Save coordinator (canvas load S.1). Every canvas write goes through one FIFO queue per element, with at
 * most one request in flight per element. The rules are in docs/canvas-load/S.0-save-rules.md.
 *
 * The queues live in this module, not in Pinia state: payloads are not observed, components can unmount
 * while their writes continue, and `clearAllStores()` (logout) clears them through its hook. The store's
 * state only mirrors what the UI shows (counts, failed writes, the header save status).
 */

export type SaveWriteKind = 'content' | 'structural'

/** Provided by a write that creates its element: every other write on that element depends on it. */
export const ELEMENT_KEY = 'element'

export interface SaveWrite<T = unknown> {
  elementId: string
  /** `content` replaces one field or cell; `structural` creates, deletes, moves or changes settings */
  kind: SaveWriteKind
  /** Field or cell inside the element (e.g. `message`, `cell:<rowId>:<columnId>`) */
  target?: string
  /** Full replacements are safe to resend; creates, deletes and moves are not */
  idempotent: boolean
  /**
   * Writes whose request body carries the whole group as stored (e.g. one FAQ entry): a later success in the group
   * supersedes a parked write of another field in it, since that body already held the parked change
   */
  group?: string
  /** The request body is built from the stored element when sent, so it carries every earlier local change */
  wholeElement?: boolean
  /** The optimistic local change: applied when queued, and again after every server write until acknowledged */
  patch?: () => void
  /** Sends the request. Must not go through ElementHistoryStore (its queue is global; see whenSettled) */
  send: () => Promise<T>
  /** Writes the response into the store. Pending patches are re-applied right after */
  apply?: (response: T) => void
  /** After a timeout: does the element on the server already hold this write? */
  verify?: (serverElement: Element) => boolean
  /** Keys this write creates (e.g. `row:<id>`); if it fails, queued writes that require them are dropped */
  provides?: string[]
  requires?: string[]
  label?: string
  /**
   * An element create: when it fails for good it is parked and its element's queue is held, so the writes typed
   * into the new block wait (nothing is sent to a block the server does not have, nothing is dropped) until
   * `retryFailed()` resends the create first. A 404 parks it too: the block keeps what the user did
   */
  holdsQueueOnPark?: boolean
  /** Before any resend, ask the server (`verify`) whether it already holds the write; a create must never repeat */
  verifyBeforeResend?: boolean
  /** The element `verify` checks, when not `elementId` (a replace checks the new element) */
  verifyElementId?: string
  /** Writes the server's element into the store when a check showed the server already holds the write */
  applyVerified?: (serverElement: Element) => void
  /** A write sent once (`idempotent: false`) failed: undo its local effect (a delete restores the element) */
  onFailure?: (error: unknown) => void
  /** A 404 means the goal is reached (deleting what is already gone) */
  succeedsOn404?: boolean
  /** This write is sent only after that element's create has reached the server (a child block). */
  afterElementId?: string
  /** False when the caller shows its own error; the element is still refetched */
  reportsFailure?: boolean
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed'

export type FailedSave = {
  id: number
  elementId: string
  target?: string
  label?: string
}

/** A write that failed for good. Its payload is kept, and `retryFailed()` resends it to its own target. */
export class SaveParkedError extends Error {
  constructor(readonly error: unknown, readonly label?: string) {
    super('The change could not be saved; it is kept for a retry')
    this.name = 'SaveParkedError'
  }
}

/** A queued write that was never sent because a write it depended on failed. */
export class SaveDroppedError extends Error {
  constructor(readonly error: unknown, readonly label?: string) {
    super('The change was not saved because an earlier change failed')
    this.name = 'SaveDroppedError'
  }
}

export const RETRY_DELAYS_MS = [1000, 3000, 10000]

/** ±20% so clients that failed together (an outage) do not all resend at the same moment. Exact under test. */
function jittered(ms: number): number {
  return import.meta.env.MODE === 'test' ? ms : Math.round(ms * (0.8 + Math.random() * 0.4))
}

type EntryState = 'queued' | 'in-flight' | 'parked'

type Waiter = {
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
}

type Entry = {
  id: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  write: SaveWrite<any>
  localRev: number
  state: EntryState
  waiters: Waiter[]
  /** Resolves once the entry has left the queue (acknowledged, dropped or parked) */
  settled: Promise<void>
  markSettled: () => void
  /** The parent create is parked: this entry stays queued and is not in flight until that create lands. */
  waitingOnParent?: boolean
  /** The error that parked this entry, when it is parked. */
  parkError?: unknown
}

type ElementQueue = {
  /** Queued and in-flight entries, FIFO; the first one is in flight while `running` */
  entries: Entry[]
  parked: Entry[]
  localRev: number
  running: boolean
  /** A parked create holds the queue: nothing else is sent for the element until it is retried */
  held: boolean
}

type SaveEvent = { type: 'parked' | 'failed'; elementId: string; error: unknown; label?: string }
type SaveEventListener = (event: SaveEvent) => void

const queues = new Map<string, ElementQueue>()
/** Element ids whose create is queued until this parent id exists on the server. */
const childrenWaitingOn = new Map<string, Set<string>>()
/** Edits typed but not yet queued (autosave timer running), by editor key */
const pendingEdits = new Map<string, string | null>()
const listeners = new Set<SaveEventListener>()
/** Called when an element's queue starts waiting for Retry, by element id (a batch stops waiting on it). */
const blockedListeners = new Map<string, Set<() => void>>()
const pendingDelays = new Set<{ timer: number; resolve: () => void }>()
let entrySeq = 0
/** Bumped by `clearSaveQueues()`; send loops from an older generation stop where they are */
let generation = 0
let hasSavedInVisit = false
let clearHookRegistered = false

/**
 * Registered on first use, not at import: AuthStore and the element stores import each other indirectly,
 * and AuthStore's hook set may not exist yet while this module is first evaluated.
 */
function ensureClearHook() {
  if (!clearHookRegistered) {
    clearHookRegistered = true
    onClearAllStores(clearSaveQueues)
  }
}

function getQueue(elementId: string): ElementQueue {
  ensureClearHook()
  let queue = queues.get(elementId)
  if (!queue) {
    queue = { entries: [], parked: [], localRev: 0, running: false, held: false }
    queues.set(elementId, queue)
  }
  return queue
}

/** Forgets an element's queue once it has nothing queued, in flight or parked, so the map stays small. */
function releaseIfIdle(elementId: string, queue: ElementQueue) {
  if (!queue.running && !queue.entries.length && !queue.parked.length && queues.get(elementId) === queue) {
    queues.delete(elementId)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function newEntry(write: SaveWrite<any>, localRev: number): Entry {
  let markSettled: () => void = () => {}
  const settled = new Promise<void>(resolve => (markSettled = resolve))
  return { id: ++entrySeq, write, localRev, state: 'queued', waiters: [], settled, markSettled }
}

function addWaiter<T>(entry: Entry): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve, reject) => {
    entry.waiters.push({ resolve: resolve as (value: unknown) => void, reject })
  })
}

function stopWaitingOnParent(entry: Entry) {
  if (!entry.waitingOnParent) return
  entry.waitingOnParent = false
  const parentId = entry.write.afterElementId
  if (!parentId) return
  const waiting = childrenWaitingOn.get(parentId)
  if (!waiting) return
  waiting.delete(entry.write.elementId)
  if (!waiting.size) childrenWaitingOn.delete(parentId)
}

function resolveEntry(entry: Entry, value: unknown) {
  stopWaitingOnParent(entry)
  entry.waiters.splice(0).forEach(waiter => waiter.resolve(value))
  entry.markSettled()
}

function rejectEntry(entry: Entry, error: unknown) {
  entry.waiters.splice(0).forEach(waiter => waiter.reject(error))
  entry.markSettled()
}

function emit(event: SaveEvent) {
  recordSaveCoordinatorEvent(event.type)
  listeners.forEach(listener => {
    try {
      listener(event)
    } catch (error) {
      console.warn('[SaveCoordinator] listener failed', error)
    }
  })
}

function delay(ms: number): { promise: Promise<void>; cancel: () => void } {
  let cancel: () => void = () => {}
  const promise = new Promise<void>(resolve => {
    const pending = {
      timer: 0,
      resolve: () => {
        pendingDelays.delete(pending)
        resolve()
      }
    }
    pending.timer = window.setTimeout(pending.resolve, ms)
    pendingDelays.add(pending)
    cancel = () => {
      window.clearTimeout(pending.timer)
      pending.resolve()
    }
  })
  return { promise, cancel }
}

function isDeleted(elementId: string): boolean {
  try {
    return useElementStore().isDeletedElement(elementId)
  } catch {
    return false
  }
}

function isLoggingOut(): boolean {
  try {
    return useAuthStore().logoutLoading
  } catch {
    return false
  }
}

type FailureKind = 'retry' | 'timeout' | 'drop' | 'park'

function classifyFailure(error: unknown): FailureKind {
  if (isLoggingOut()) return 'drop'
  if (isNetworkError(error)) {
    return (error as { code?: string }).code === 'ECONNABORTED' ? 'timeout' : 'retry'
  }
  const status = getApiErrorStatus(error)
  // Not an HTTP failure: a bug in a send() or apply path. Keep the change rather than lose it silently.
  if (status === undefined) return 'park'
  // The target is gone (deleted elsewhere); there is nothing left to save it into
  if (status === 404) return 'drop'
  // ApiClient already refreshed the token and retried once; when that fails it logs out
  if (status === 401) return 'drop'
  if (status >= 500) return 'retry'
  // Request timeout, too early, rate limited: transient, a later resend can succeed
  if (status === 408 || status === 425 || status === 429) return 'retry'
  // Other 4xx, the trial soft-lock / entitlement 403s, a signed document: a resend would fail the same way
  return 'park'
}

/** A queued entry that will carry `parked`'s change, so resending `parked` would only revert newer data. */
function covers(entry: Entry, parked: Entry): boolean {
  if (entry.write.wholeElement && parked.write.wholeElement) return true
  if (entry.write.group && entry.write.group === parked.write.group) return true
  return (
    entry.write.kind === 'content' &&
    parked.write.kind === 'content' &&
    !!entry.write.target &&
    entry.write.target === parked.write.target
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function canMerge(queued: Entry, write: SaveWrite<any>): boolean {
  if (queued.state !== 'queued' || queued.write.kind !== 'content' || write.kind !== 'content') return false
  if (queued.write.wholeElement && write.wholeElement) return true
  return !!write.target && queued.write.target === write.target && !queued.write.wholeElement && !write.wholeElement
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyWrite = SaveWrite<any>

/** The writes a merged whole-element write was built from: the latest one per target, in the order last written. */
const mergedParts = new WeakMap<AnyWrite, Map<string | undefined, AnyWrite>>()

/**
 * The write a queued entry carries after `write` merges into it. Whole-element writes merge across fields (one PUT
 * carries every field), so the merged entry keeps the latest local change and check of every field it absorbed,
 * however the fields alternate; a same-target write replaces only that target's older one.
 */
function mergedWrite(queued: AnyWrite, write: AnyWrite): AnyWrite {
  if (!queued.wholeElement) return write
  const parts = new Map(mergedParts.get(queued) ?? [[queued.target, queued]])
  parts.delete(write.target)
  parts.set(write.target, write)
  if (parts.size === 1) return write
  const writes = [...parts.values()]
  const merged: AnyWrite = {
    ...write,
    patch: () => writes.forEach(part => part.patch?.()),
    verify: writes.every(part => part.verify)
      ? serverElement => writes.every(part => part.verify!(serverElement))
      : undefined
  }
  mergedParts.set(merged, parts)
  return merged
}

function reapplyPatches(queue: ElementQueue) {
  ;[...queue.parked, ...queue.entries]
    .sort((a, b) => a.id - b.id)
    .forEach(entry => {
      try {
        entry.write.patch?.()
      } catch (error) {
        console.warn('[SaveCoordinator] re-applying a pending change failed', error)
      }
    })
}

function acknowledge(
  queue: ElementQueue,
  entry: Entry,
  response: unknown,
  hasResponse: boolean,
  verifiedServerElement?: Element
) {
  queue.entries.splice(queue.entries.indexOf(entry), 1)
  // A response that answers after its element was deleted here must not put the element back
  if (!isDeleted(entry.write.elementId)) {
    try {
      if (hasResponse) entry.write.apply?.(response)
      else if (verifiedServerElement) entry.write.applyVerified?.(verifiedServerElement)
    } catch (error) {
      // The server has the write; a failing apply must not turn into a resend
      console.warn('[SaveCoordinator] applying a save response failed', error)
    }
  }
  queue.parked = queue.parked.filter(parked => {
    if (parked.id < entry.id && covers(entry, parked)) {
      parked.markSettled()
      return false
    }
    return true
  })
  reapplyPatches(queue)
  hasSavedInVisit = true
  resolveEntry(entry, response)
}

/**
 * The block was deleted (or its create undone) while its create was retrying, and the server never got it: neither
 * the create nor anything queued behind it (the delete) has to be sent.
 */
function dropDeletedCreate(queue: ElementQueue) {
  queue.entries.splice(0).forEach(queued => resolveEntry(queued, undefined))
}

function park(elementId: string, queue: ElementQueue, entry: Entry, error: unknown) {
  if (entry.write.holdsQueueOnPark && isDeleted(elementId)) {
    dropDeletedCreate(queue)
    return
  }
  queue.entries.splice(queue.entries.indexOf(entry), 1)
  entry.state = 'parked'
  entry.parkError = error
  queue.parked.push(entry)
  if (entry.write.holdsQueueOnPark) {
    queue.held = true
    noteCreateParked(elementId)
  }
  rejectEntry(entry, new SaveParkedError(error, entry.write.label))
  emit({ type: 'parked', elementId, error, label: entry.write.label })
  if (entry.write.holdsQueueOnPark) noteQueueBlocked(elementId)
}

/** Nothing more is sent for the element until Retry: its create parked, or waits on a parent's parked create. */
function noteQueueBlocked(elementId: string) {
  blockedListeners.get(elementId)?.forEach(listener => {
    try {
      listener()
    } catch (error) {
      console.warn('[SaveCoordinator] blocked listener failed', error)
    }
  })
}

function drop(queue: ElementQueue, entry: Entry, value?: unknown) {
  queue.entries.splice(queue.entries.indexOf(entry), 1)
  resolveEntry(entry, value)
}

async function refetchElement(elementId: string, queue: ElementQueue) {
  const gen = generation
  try {
    const serverElement = (await ApiClient.get<Element>(`/elements/${elementId}`)).data
    // The session ended meanwhile (logout cleared the stores), or the user deleted the element since: do not put
    // it back
    if (gen !== generation || isDeleted(elementId)) return
    useElementStore().setElement(serverElement)
    reapplyPatches(queue)
  } catch {
    // The page keeps what it shows; the user was told the change failed
  }
}

/** A non-idempotent write failed: it is never resent, and writes that needed it are dropped. */
async function failNonIdempotent(elementId: string, queue: ElementQueue, entry: Entry, error: unknown) {
  queue.entries.splice(queue.entries.indexOf(entry), 1)
  const provides = entry.write.provides || []
  // A later write that provides the key again (an undone create, redone) creates it anew: writes queued after that
  // one need it, not the failed one, and stay
  const providedAgainAt = (key: string) => {
    const index = queue.entries.findIndex(queued => (queued.write.provides || []).includes(key))
    return index === -1 ? queue.entries.length : index
  }
  const dependants = queue.entries.filter((queued, index) => {
    if (queued.state !== 'queued') return false
    const needed = provides.includes(ELEMENT_KEY)
      ? [ELEMENT_KEY]
      : (queued.write.requires || []).filter(key => provides.includes(key))
    return needed.some(key => index < providedAgainAt(key))
  })
  dependants.forEach(dependant => {
    queue.entries.splice(queue.entries.indexOf(dependant), 1)
    rejectEntry(dependant, new SaveDroppedError(error, dependant.write.label))
  })
  rejectEntry(entry, error)
  try {
    entry.write.onFailure?.(error)
  } catch (failure) {
    console.warn('[SaveCoordinator] undoing a failed change locally failed', failure)
  }
  if (getApiErrorStatus(error) !== 404 && !isLoggingOut()) {
    if (entry.write.reportsFailure !== false) emit({ type: 'failed', elementId, error, label: entry.write.label })
    await refetchElement(elementId, queue)
  }
}

/** Before a resend, fold in a newer queued change to the same target, so the retry sends the newest payload. */
function absorbNewerWrite(queue: ElementQueue, entry: Entry) {
  const newer = queue.entries.find(queued => queued !== entry && canMerge(queued, entry.write))
  if (!newer) return
  queue.entries.splice(queue.entries.indexOf(newer), 1)
  entry.write = mergedWrite(entry.write, newer.write)
  entry.localRev = newer.localRev
  entry.waiters.push(...newer.waiters.splice(0))
  newer.markSettled()
}

type ServerCheck = { state: 'present'; element: Element } | { state: 'absent' } | { state: 'unknown' }

/** What the server holds for an element: it, nothing (404), or no answer. */
async function checkServer(elementId: string): Promise<ServerCheck> {
  try {
    return { state: 'present', element: (await ApiClient.get<Element>(`/elements/${elementId}`)).data }
  } catch (error) {
    return getApiErrorStatus(error) === 404 ? { state: 'absent' } : { state: 'unknown' }
  }
}

/** The server's element when it already holds the entry's write, else undefined. */
async function verifyOnServer(entry: Entry): Promise<Element | undefined> {
  if (!entry.write.verify) return undefined
  const check = await checkServer(entry.write.verifyElementId ?? entry.write.elementId)
  return check.state === 'present' && entry.write.verify(check.element) ? check.element : undefined
}

async function runEntry(elementId: string, queue: ElementQueue, entry: Entry, gen: number) {
  let lastError: unknown
  for (let attempt = 0; ; attempt++) {
    // A create is resent only once the server shows it does not have the element: a lost response must never
    // create it twice (the backend adds defaults, e.g. an offer's first row). No answer counts as a failed attempt.
    if (attempt > 0 && entry.write.verifyBeforeResend) {
      const check = await checkServer(entry.write.elementId)
      if (gen !== generation) return
      if (check.state === 'present') {
        acknowledge(queue, entry, undefined, false, check.element)
        return
      }
      if (check.state === 'absent' && entry.write.holdsQueueOnPark && isDeleted(elementId)) {
        dropDeletedCreate(queue)
        return
      }
      if (check.state === 'unknown') {
        if (attempt >= RETRY_DELAYS_MS.length) {
          park(elementId, queue, entry, lastError)
          return
        }
        await delay(jittered(RETRY_DELAYS_MS[attempt])).promise
        if (gen !== generation) return
        continue
      }
    }
    let response: unknown
    // Bodies are built from the store: a write outside the coordinator or a page refetch may have replaced it with
    // older server state since this change was queued (a retry waits seconds)
    reapplyPatches(queue)
    try {
      response = await entry.write.send()
    } catch (error) {
      if (gen !== generation) return
      if (error instanceof ParentCreateParked) {
        // Deleted meanwhile: like a create that parks, neither it nor the delete queued behind it is sent
        if (entry.write.holdsQueueOnPark && isDeleted(elementId)) {
          dropDeletedCreate(queue)
          return
        }
        entry.state = 'queued'
        entry.waitingOnParent = true
        const parentId = entry.write.afterElementId
        if (parentId) {
          const waiting = childrenWaitingOn.get(parentId) ?? new Set<string>()
          waiting.add(elementId)
          childrenWaitingOn.set(parentId, waiting)
        }
        noteQueueBlocked(elementId)
        return
      }
      lastError = error
      // The coordinator reports failures itself (header status, snackbar); no generic network snackbar
      claimNetworkError(error)
      const failure = classifyFailure(error)
      const status = getApiErrorStatus(error)
      if (entry.write.succeedsOn404 && status === 404) {
        acknowledge(queue, entry, undefined, false)
        return
      }
      // A timeout may hide a write the server did make (S.0 timeout row)
      if (failure === 'timeout' && entry.write.succeedsOn404) {
        const check = await checkServer(entry.write.elementId)
        if (gen !== generation) return
        if (check.state === 'absent') {
          acknowledge(queue, entry, undefined, false)
          return
        }
      } else if (failure === 'timeout' && !entry.write.verifyBeforeResend) {
        const verified = await verifyOnServer(entry)
        if (gen !== generation) return
        if (verified) {
          acknowledge(queue, entry, undefined, false, verified)
          return
        }
      }
      if (!entry.write.idempotent) {
        await failNonIdempotent(elementId, queue, entry, error)
        return
      }
      // A create the server answers 404 (its page is gone) keeps the block for Retry; an ended session drops it
      const keepCreate = entry.write.holdsQueueOnPark && status === 404
      if (failure === 'drop' && !keepCreate) {
        // A 404 for an element this tab does not know to be deleted (removed elsewhere): the text on screen was never
        // stored. A resend cannot succeed, so no Retry; one warning instead. A session that ended stays silent.
        if (status === 404 && !isLoggingOut() && !isDeleted(elementId) && entry.write.reportsFailure !== false) {
          emit({ type: 'failed', elementId, error, label: entry.write.label })
        }
        drop(queue, entry)
        return
      }
      if (failure === 'park' || keepCreate || attempt >= RETRY_DELAYS_MS.length) {
        park(elementId, queue, entry, error)
        return
      }
      await delay(jittered(RETRY_DELAYS_MS[attempt])).promise
      if (gen !== generation) return
      absorbNewerWrite(queue, entry)
      continue
    }
    if (gen !== generation) return
    acknowledge(queue, entry, response, true)
    return
  }
}

async function pump(elementId: string, queue: ElementQueue) {
  if (queue.running) return
  queue.running = true
  const gen = generation
  try {
    while (gen === generation && queue.entries.length && !queue.held) {
      const entry = queue.entries[0]
      if (entry.waitingOnParent) break
      entry.state = 'in-flight'
      syncState()
      await runEntry(elementId, queue, entry, gen)
      syncState()
      if (entry.waitingOnParent) break
    }
  } finally {
    if (gen === generation) {
      queue.running = false
      releaseIfIdle(elementId, queue)
      syncState()
    }
  }
}

function enqueueWrite<T>(write: SaveWrite<T>): Promise<T | undefined> {
  const queue = getQueue(write.elementId)
  queue.localRev += 1
  write.patch?.()
  const mergeInto = queue.entries.find(queued => canMerge(queued, write))
  let promise: Promise<T | undefined>
  if (mergeInto) {
    mergeInto.write = mergedWrite(mergeInto.write, write)
    mergeInto.localRev = queue.localRev
    promise = addWaiter<T>(mergeInto)
  } else {
    const entry = newEntry(write, queue.localRev)
    promise = addWaiter<T>(entry)
    queue.entries.push(entry)
  }
  // Failures are reported by the coordinator itself (header status, snackbar). Marking the promise handled
  // keeps a caller that does not await it from raising an unhandled rejection; a caller's own catch still runs.
  promise.catch(() => {})
  syncState()
  void pump(write.elementId, queue)
  return promise
}

function retryParked() {
  recordSaveRetryInvoked()
  queues.forEach((queue, elementId) => {
    if (!queue.parked.length) return
    const parked = queue.parked.splice(0)
    const creates: Entry[] = []
    parked.forEach(entry => {
      // A newer write of the same content supersedes it, whether still queued, in flight or waiting to retry: the
      // requeued entry keeps its older id, so it would re-apply and send the older value after the newer one
      if (queue.entries.some(queued => queued.id > entry.id && covers(queued, entry))) {
        return
      }
      const requeued = newEntry(entry.write, entry.localRev)
      requeued.id = entry.id
      if (entry.write.holdsQueueOnPark) creates.push(requeued)
      else queue.entries.push(requeued)
    })
    // A held create goes first: everything queued behind it needs the element on the server
    if (creates.length) {
      queue.entries.unshift(...creates)
      queue.held = false
    }
    syncState()
    void pump(elementId, queue)
  })
}

function whenQueuesSettled(elementIds: string[] | null, timeoutMs: number): Promise<void> {
  const ids = elementIds ?? [...queues.keys()]
  // A held queue (failed create) waits for Retry: like parked writes, its entries count as settled
  const pending = ids
    .flatMap(id => {
      const queue = queues.get(id)
      return queue && !queue.held ? queue.entries : []
    })
    .map(entry => entry.settled)
  if (!pending.length) return Promise.resolve()
  const timeout = delay(timeoutMs)
  return Promise.race([Promise.all(pending).then(() => undefined), timeout.promise]).then(timeout.cancel)
}

/**
 * The element was deleted on the server: its parked writes have nowhere to go. Queued writes stay, since they may
 * already belong to the element's re-creation (undo of the delete is queued behind it).
 */
function purge(elementId: string) {
  const queue = queues.get(elementId)
  if (!queue) return
  queue.parked.splice(0).forEach(entry => entry.markSettled())
  queue.held = false
  releaseIfIdle(elementId, queue)
  syncState()
}

/**
 * The element's queue waits for Retry: its create is parked, or waits on a parent's parked create. A batch must not
 * wait on that element.
 */
export function isSaveQueueHeld(elementId: string): boolean {
  const queue = queues.get(elementId)
  return !!queue && (queue.held || !!queue.entries[0]?.waitingOnParent)
}

/** Calls `listener` each time the element's queue starts waiting for Retry. Returns the unsubscribe. */
export function onSaveQueueBlocked(elementId: string, listener: () => void): () => void {
  const set = blockedListeners.get(elementId) ?? new Set<() => void>()
  set.add(listener)
  blockedListeners.set(elementId, set)
  return () => {
    set.delete(listener)
    if (!set.size && blockedListeners.get(elementId) === set) blockedListeners.delete(elementId)
  }
}

/**
 * Takes a write that has not been sent out of its element's queue; its caller gets `undefined`. Returns false when it
 * is no longer queued (sent, or done).
 */
export function withdrawQueuedWrite(elementId: string, write: AnyWrite): boolean {
  const queue = queues.get(elementId)
  const entry = queue?.entries.find(queued => queued.write === write && queued.state === 'queued')
  if (!queue || !entry) return false
  drop(queue, entry)
  releaseIfIdle(elementId, queue)
  syncState()
  return true
}

/**
 * Deleting (or undoing) a block whose create has not been sent: drop that create and every write queued behind it,
 * so nothing is sent. Writes queued before it stay (a delete queued before an undo's re-create must still go).
 * Returns false when the newest create is in flight or already saved; the delete must then be queued as usual.
 */
export function cancelUnsavedCreate(elementId: string): boolean {
  const queue = queues.get(elementId)
  if (!queue) return false
  let dropped: Entry[] = []
  if (queue.held) {
    // The parked create comes first: everything queued is behind it
    queue.parked.filter(entry => entry.write.holdsQueueOnPark).forEach(entry => entry.markSettled())
    queue.parked = queue.parked.filter(entry => !entry.write.holdsQueueOnPark)
    dropped = queue.entries.splice(0)
    queue.held = false
  } else {
    let index = -1
    queue.entries.forEach((entry, i) => {
      if (entry.write.holdsQueueOnPark) index = entry.state === 'queued' ? i : -1
    })
    if (index < 0) return false
    dropped = queue.entries.splice(index)
  }
  dropped.forEach(entry => resolveEntry(entry, undefined))
  releaseIfIdle(elementId, queue)
  syncState()
  return true
}

/**
 * Changes on their way to the server: typed and not queued yet, queued, in flight, or waiting on a retry. Parked
 * writes (and writes held behind a parked create) do not count: they wait for Retry, possibly forever (the server
 * refused them), and the header status and snackbar already tell the user.
 */
export function hasUnsavedChanges(): boolean {
  if (pendingEdits.size) return true
  for (const queue of queues.values()) {
    if (queue.held) continue
    if (queue.entries.some(entry => !entry.waitingOnParent)) return true
  }
  return false
}

/** Parked because the server rejected the write (4xx). A retry would fail the same way; closing the tab may drop it. */
function isPermanentRejection(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  return status !== undefined && status >= 400 && status < 500
}

/**
 * Parked writes that are not a permanent 4xx: retries ran out, or the failure was not an HTTP rejection.
 * They live only in this tab.
 */
export function hasParkedChangesAtRisk(): boolean {
  for (const queue of queues.values()) {
    if (queue.parked.some(entry => !isPermanentRejection(entry.parkError))) return true
  }
  return false
}

/** The parent was deleted before the server had it: its children's creates are cancelled, nothing waits on it. */
export function forgetCreatesWaitingOn(parentId: string) {
  childrenWaitingOn.delete(parentId)
}

/** Starts children that were queued behind a parent create which has now reached the server. */
export function resumeCreatesWaitingOn(parentId: string) {
  const ids = childrenWaitingOn.get(parentId)
  if (!ids?.size) return
  childrenWaitingOn.delete(parentId)
  let resumed = false
  ids.forEach(elementId => {
    const queue = queues.get(elementId)
    if (!queue) return
    let resume = false
    queue.entries.forEach(entry => {
      if (entry.waitingOnParent && entry.write.afterElementId === parentId) {
        entry.waitingOnParent = false
        resume = true
      }
    })
    if (resume) {
      resumed = true
      void pump(elementId, queue)
    }
  })
  if (resumed) syncState()
}

/**
 * Queues the edit being typed in the focused field: every editor and text field saves on blur. Only while changes
 * are on their way, so nobody loses focus for nothing.
 */
export function queueFocusedEdit(): void {
  if (!hasUnsavedChanges()) return
  const active = document.activeElement
  if (active instanceof HTMLElement && active !== document.body) active.blur()
}

/**
 * Before the page's content goes away (route change, logout): the edit being typed is queued, then every queued
 * write gets up to `timeoutMs` to finish. Parked writes do not hold it up; they stay in this tab for Retry.
 */
export async function flushPendingSaves(timeoutMs = 2000): Promise<void> {
  if (!hasUnsavedChanges()) return
  queueFocusedEdit()
  // Blur handlers queue their saves on the same tick or the next one
  await Promise.resolve()
  await useSaveCoordinatorStore().flush(timeoutMs)
}

// Closing or reloading the tab while changes are on their way to the server asks the user first
function warnBeforeUnload(event: BeforeUnloadEvent) {
  if (!hasUnsavedChanges() && !hasParkedChangesAtRisk()) return
  event.preventDefault()
  event.returnValue = ''
}
// A re-evaluated module (HMR) replaces its predecessor's listener instead of stacking another
type UnloadHost = Window & { __notchSaveUnload?: (event: BeforeUnloadEvent) => void }
const unloadHost = window as UnloadHost
if (unloadHost.__notchSaveUnload) window.removeEventListener('beforeunload', unloadHost.__notchSaveUnload)
unloadHost.__notchSaveUnload = warnBeforeUnload
window.addEventListener('beforeunload', warnBeforeUnload)

/** Drops every queue without sending (logout, switching sessions). Callers waiting on a write get `undefined`. */
export function clearSaveQueues() {
  generation += 1
  childrenWaitingOn.clear()
  blockedListeners.clear()
  queues.forEach(queue => {
    ;[...queue.entries, ...queue.parked].forEach(entry => resolveEntry(entry, undefined))
  })
  queues.clear()
  pendingEdits.clear()
  pendingDelays.forEach(pending => {
    window.clearTimeout(pending.timer)
    pending.resolve()
  })
  hasSavedInVisit = false
  syncState()
}

/**
 * Queues a write for its element. Resolves with the response once the server has it (or `undefined` when it
 * was dropped: its target is gone, or the session ended); rejects with SaveParkedError when it failed for good,
 * SaveDroppedError when a write it needed failed, or the request error for a failed non-idempotent write.
 *
 * A plain function, not a store action: Pinia wraps an action's promise in a new one that rethrows, which
 * would turn every failure a caller does not await into an unhandled rejection.
 */
export function enqueueSave<T>(write: SaveWrite<T>): Promise<T | undefined> {
  return enqueueWrite(write)
}

/** Listens for writes that failed for good (`parked`) and non-idempotent writes that failed (`failed`). */
export function onSaveEvent(listener: SaveEventListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// A change that did not reach the server is never lost silently. A parked change is kept in this tab: one snackbar
// with Retry, which resends every parked write. A change sent once that failed was refetched over: one deduplicated
// warning, shared with the editors' own save-failed snackbar
onSaveEvent(event => {
  if (event.type === 'parked') {
    enqueueUnsavedChangeRetrySnackbar(event.error, () => useSaveCoordinatorStore().retryFailed())
  } else {
    enqueueUnsavedChangeSnackbar(event.error)
  }
})

type SaveCoordinatorState = {
  pendingCount: number
  editingCount: number
  failed: FailedSave[]
  hasSavedInVisit: boolean
}

function syncState() {
  let store: SaveCoordinatorStore
  try {
    store = useSaveCoordinatorStore()
  } catch {
    return
  }
  let pendingCount = 0
  const failed: FailedSave[] = []
  queues.forEach((queue, elementId) => {
    pendingCount += queue.entries.filter(entry => !entry.waitingOnParent).length
    queue.parked.forEach(entry =>
      failed.push({ id: entry.id, elementId, target: entry.write.target, label: entry.write.label })
    )
  })
  const editingCount = pendingEdits.size
  // Called on every queue change; only touch reactive state when the UI would see a difference
  const failedChanged =
    failed.length !== store.failed.length || failed.some((item, index) => item.id !== store.failed[index].id)
  if (
    !failedChanged &&
    store.pendingCount === pendingCount &&
    store.editingCount === editingCount &&
    store.hasSavedInVisit === hasSavedInVisit
  ) {
    return
  }
  store.$patch(state => {
    state.pendingCount = pendingCount
    state.editingCount = editingCount
    state.hasSavedInVisit = hasSavedInVisit
    if (failedChanged) state.failed = failed
  })
  // Nothing is parked any more (saved by Retry, superseded, or its element deleted): the Retry snackbar is stale
  if (failedChanged && !failed.length) dismissUnsavedChangeRetrySnackbar()
}

export const useSaveCoordinatorStore = defineStore('saveCoordinator', {
  state: (): SaveCoordinatorState => ({
    pendingCount: 0,
    editingCount: 0,
    failed: [],
    hasSavedInVisit: false
  }),
  getters: {
    saveStatus(state): SaveStatus {
      if (state.failed.length) return 'failed'
      if (state.pendingCount || state.editingCount) return 'saving'
      return state.hasSavedInVisit ? 'saved' : 'idle'
    }
  },
  actions: {
    /** Resends every parked write, each to its own target with its own payload. */
    retryFailed() {
      retryParked()
    },
    /**
     * Resolves once the writes queued for these elements now have finished, or after `timeoutMs`. Parked
     * writes count as finished, and writes queued later are not waited for. Call it before
     * `ElementHistoryStore.doAction`, never inside a reversible action.
     */
    whenSettled(elementIds: string[], timeoutMs = 10000): Promise<void> {
      return whenQueuesSettled(elementIds, timeoutMs)
    },
    /** Waits up to `timeoutMs` for every queued write (route leave, logout). */
    flush(timeoutMs = 2000): Promise<void> {
      return whenQueuesSettled(null, timeoutMs)
    },
    /** An editor has typed changes its autosave has not queued yet (keyed per editor). */
    setEditPending(key: string, elementId: string | null, pending: boolean) {
      ensureClearHook()
      if (pending) {
        pendingEdits.set(key, elementId)
      } else {
        pendingEdits.delete(key)
      }
      syncState()
    },
    /** True while the element has typed, queued, in-flight or parked changes: do not push stored content into its editor. */
    hasLocalChanges(elementId: string): boolean {
      const queue = queues.get(elementId)
      if (queue && (queue.entries.length || queue.parked.length)) return true
      for (const editElementId of pendingEdits.values()) {
        if (editElementId === elementId) return true
      }
      return false
    },
    /** Applies the pending changes again after the store was refilled from the server (page fetch, reload). */
    reapplyPendingPatches(elementIds?: string[]) {
      const ids = elementIds ?? [...queues.keys()]
      ids.forEach(id => {
        const queue = queues.get(id)
        if (queue) reapplyPatches(queue)
      })
    },
    /** The element was deleted on the server: its parked writes have nowhere to go. */
    purgeElement(elementId: string) {
      purge(elementId)
    },
    /** A new space visit starts at `idle`, not at the previous space's `saved`. */
    resetVisit() {
      hasSavedInVisit = false
      syncState()
    }
  }
})

export type SaveCoordinatorStore = ReturnType<typeof useSaveCoordinatorStore>
